import { ApolloError, useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { TreeSelectOption } from '../../../components/TreeSelect/TreeSelect';
import { Option } from '../../../components/types';
import { handleGql403 } from '../../../utils';
import { GetAdvertisersByOrgIdQuery, GetAdvertisersByOrgIdQueryVariables } from '../types';
import { GET_ADVERTISERS_BY_ORG_ID } from './advertisers';

export type UseGetAdvertisersByOrgId = {
  advertisersWithAdAccounts: TreeSelectOption[];
  advertisersOptions: Option[];
  adAccountList: Record<string, Array<string>>;
  loading: boolean;
  error: ApolloError | undefined;
};

const useGetAdvertisersByOrgId = (
  orgId: string = '',
  accessTypes: Array<string> = ['external_visible'],
  skip: boolean = false
): UseGetAdvertisersByOrgId => {
  const { data, loading, error } = useQuery<GetAdvertisersByOrgIdQuery, GetAdvertisersByOrgIdQueryVariables>(
    GET_ADVERTISERS_BY_ORG_ID,
    {
      variables: {
        orgId,
        accessTypes,
      },
      skip: !orgId || skip,
      onError: handleGql403,
      fetchPolicy: 'no-cache',
    }
  );

  const memoizedData = useMemo(() => {
    const advertisersWithAdAccounts: TreeSelectOption[] = [];
    const advertisersOptions: UseGetAdvertisersByOrgId['advertisersOptions'] = [];
    const adAccountList: UseGetAdvertisersByOrgId['adAccountList'] = {};
    const advertisers: Record<string, { adAccounts: string[] }> = {};

    data?.getAdvertisersByOrgId.advertisers.forEach(({ advertiserId, advertiserName, adAccounts }) => {
      if (!advertisers[advertiserId]) {
        const children: TreeSelectOption[] = [];
        adAccounts.forEach((adAccount) => {
          if (!advertisers[advertiserId]?.adAccounts?.includes(adAccount.id)) {
            children.push({ title: adAccount.name, value: adAccount.id, key: adAccount.id });
          }
        });
        advertisersWithAdAccounts.push({
          title: advertiserName,
          value: advertiserId,
          key: advertiserId,
          children,
        });
      }

      advertisersOptions.push({ value: advertiserId, label: advertiserName });
      adAccountList[advertiserId] = adAccounts.map((item) => item.id);
      advertisers[advertiserId] = { adAccounts: adAccounts.map((item) => item.id) };
    });

    advertisersOptions.sort((a, b) => a.label.localeCompare(b.label));

    return {
      advertisersWithAdAccounts,
      advertisersOptions,
      adAccountList,
    };
  }, [data]);

  return {
    ...memoizedData,
    loading,
    error,
  };
};

export default useGetAdvertisersByOrgId;
